import React, { useState, useRef, useEffect } from 'react';
import {
  Card,
  Button,
  Input,
  Space,
  Alert,
  Table,
  Modal,
  Form,
  Row,
  Col,
  Typography,
  Divider,
  message,
  Tag,
  Select,
} from 'antd';
import {
  ScanOutlined,
  CameraOutlined,
  StopOutlined,
  PlusOutlined,
  MinusOutlined,
  CheckOutlined,
  PrinterOutlined,
} from '@ant-design/icons';
// import useStore from '../store/useStore'; // Unused for now
import { inventoryAPI, barcodeAPI } from '../services/api';


const { Text, Title } = Typography;

const BarcodeScanner = () => {
  // const { products } = useStore(); // Unused for now
  const [scanning, setScanning] = useState(false);
  const [manualBarcode, setManualBarcode] = useState('');
  const [scannedProducts, setScannedProducts] = useState([]);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [adjustmentModalVisible, setAdjustmentModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [form] = Form.useForm();
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    return () => {
      stopScanning();
    };
  }, []);

  const startScanning = async () => {
    try {
      setError(null);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        setScanning(true);
      }
    } catch (err) {
      setError('Camera access denied or not available');
      console.error('Error accessing camera:', err);
    }
  };

  const stopScanning = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setScanning(false);
  };

  // const handleBarcodeDetected = async (barcode) => {
  //   await processBarcode(barcode);
  // };

  const handleManualScan = async () => {
    if (manualBarcode.trim()) {
      await processBarcode(manualBarcode.trim());
      setManualBarcode('');
    }
  };

  const processBarcode = async (barcode) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await barcodeAPI.scan(barcode);
      const product = response.data;
      
      if (product) {
        setCurrentProduct(product);
        addToScannedList(product, barcode);
        message.success(`Product found: ${product.name}`);
      } else {
        message.warning('Product not found for this barcode');
      }
    } catch (err) {
      setError('Failed to process barcode');
      message.error('Failed to process barcode');
    } finally {
      setLoading(false);
    }
  };

  const addToScannedList = (product, barcode) => {
    setScannedProducts(prev => {
      const existing = prev.find(item => item.barcode === barcode);
      if (existing) {
        return prev.map(item =>
          item.barcode === barcode
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      } else {
        return [...prev, {
          ...product,
          barcode,
          quantity: 1,
          scannedAt: new Date(),
        }];
      }
    });
  };

  const updateQuantity = (barcode, change) => {
    setScannedProducts(prev =>
      prev.map(item => {
        if (item.barcode === barcode) {
          const newQuantity = Math.max(0, item.quantity + change);
          return { ...item, quantity: newQuantity };
        }
        return item;
      }).filter(item => item.quantity > 0)
    );
  };

  const removeItem = (barcode) => {
    setScannedProducts(prev => prev.filter(item => item.barcode !== barcode));
  };

  const handleStockAdjustment = () => {
    if (scannedProducts.length === 0) {
      message.warning('No items scanned');
      return;
    }
    setAdjustmentModalVisible(true);
  };

  const processStockAdjustment = async () => {
    try {
      const values = await form.validateFields();
      
      const adjustments = scannedProducts.map(item => ({
        productId: item.id,
        quantity: values.adjustmentType === 'add' ? item.quantity : -item.quantity,
        reason: values.reason,
        location: values.location,
      }));

      await inventoryAPI.adjustStock(adjustments);
      
      message.success('Stock adjustment completed successfully');
      setScannedProducts([]);
      setAdjustmentModalVisible(false);
      form.resetFields();
    } catch (err) {
      message.error('Failed to process stock adjustment');
    }
  };

  const generateBarcode = async (productId) => {
    try {
      await barcodeAPI.generate(productId);
      message.success('Barcode generated successfully');
    } catch (err) {
      message.error('Failed to generate barcode');
    }
  };

  const printLabels = async () => {
    try {
      const barcodes = scannedProducts.map(item => item.barcode);
      await barcodeAPI.print(barcodes);
      message.success('Labels sent to printer');
    } catch (err) {
      message.error('Failed to print labels');
    }
  };

  const scannedColumns = [
    {
      title: 'Product',
      key: 'product',
      render: (_, record) => (
        <div>
          <div className="font-medium">{record.name}</div>
          <div className="text-sm text-gray-500">SKU: {record.sku}</div>
          <div className="text-sm text-gray-500">Barcode: {record.barcode}</div>
        </div>
      ),
    },
    {
      title: 'Current Stock',
      dataIndex: 'currentStock',
      key: 'currentStock',
    },
    {
      title: 'Scanned Qty',
      key: 'quantity',
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<MinusOutlined />}
            onClick={() => updateQuantity(record.barcode, -1)}
          />
          <span className="mx-2 font-medium">{record.quantity}</span>
          <Button
            size="small"
            icon={<PlusOutlined />}
            onClick={() => updateQuantity(record.barcode, 1)}
          />
        </Space>
      ),
    },
    {
      title: 'Scanned At',
      dataIndex: 'scannedAt',
      key: 'scannedAt',
      render: (date) => new Date(date).toLocaleTimeString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button
          size="small"
          danger
          onClick={() => removeItem(record.barcode)}
        >
          Remove
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-slate-100 mb-2">Barcode Scanner</h1>
        <p className="text-gray-600">Scan barcodes to track inventory and manage stock</p>
      </div>

      <Row gutter={16}>
        {/* Scanner Section */}
        <Col xs={24} lg={12}>
          <Card title="Scanner" className="h-full">
            <div className="space-y-4">
              {/* Camera Scanner */}
              <div className="text-center">
                {scanning ? (
                  <div className="space-y-4">
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      className="w-full max-w-sm border rounded-lg"
                      style={{ maxHeight: '300px' }}
                    />
                    <Button
                      type="primary"
                      danger
                      icon={<StopOutlined />}
                      onClick={stopScanning}
                    >
                      Stop Scanning
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="w-full h-48 bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                      <div className="text-center">
                        <CameraOutlined className="text-4xl text-gray-400 mb-2" />
                        <p className="text-gray-500">Camera preview will appear here</p>
                      </div>
                    </div>
                    <Button
                      type="primary"
                      icon={<CameraOutlined />}
                      onClick={startScanning}
                      size="large"
                    >
                      Start Camera Scanner
                    </Button>
                  </div>
                )}
              </div>

              <Divider>OR</Divider>

              {/* Manual Input */}
              <div className="space-y-4">
                <Text strong>Manual Barcode Entry</Text>
                <Space.Compact style={{ width: '100%' }}>
                  <Input
                    placeholder="Enter barcode manually"
                    value={manualBarcode}
                    onChange={(e) => setManualBarcode(e.target.value)}
                    onPressEnter={handleManualScan}
                    disabled={loading}
                  />
                  <Button
                    type="primary"
                    icon={<ScanOutlined />}
                    onClick={handleManualScan}
                    loading={loading}
                  >
                    Scan
                  </Button>
                </Space.Compact>
              </div>

              {error && (
                <Alert
                  message="Error"
                  description={error}
                  type="error"
                  showIcon
                  closable
                  onClose={() => setError(null)}
                />
              )}
            </div>
          </Card>
        </Col>

        {/* Current Product Info */}
        <Col xs={24} lg={12}>
          <Card title="Product Information" className="h-full">
            {currentProduct ? (
              <div className="space-y-4">
                <div className="text-center">
                  <Title level={4}>{currentProduct.name}</Title>
                  <Text type="secondary">SKU: {currentProduct.sku}</Text>
                </div>
                
                <Divider />
                
                <Row gutter={16}>
                  <Col span={12}>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">
                        {currentProduct.currentStock}
                      </div>
                      <div className="text-sm text-gray-500">Current Stock</div>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">
                        ${currentProduct.price?.toFixed(2)}
                      </div>
                      <div className="text-sm text-gray-500">Price</div>
                    </div>
                  </Col>
                </Row>

                <Divider />

                <div className="space-y-2">
                  <div><Text strong>Category:</Text> {currentProduct.categoryName || 'Uncategorized'}</div>
                  <div><Text strong>Reorder Point:</Text> {currentProduct.reorderPoint}</div>
                  <div>
                    <Text strong>Status:</Text>{' '}
                    <Tag color={currentProduct.currentStock <= currentProduct.reorderPoint ? 'red' : 'green'}>
                      {currentProduct.currentStock <= currentProduct.reorderPoint ? 'Low Stock' : 'In Stock'}
                    </Tag>
                  </div>
                </div>

                <Button
                  type="dashed"
                  block
                  icon={<PrinterOutlined />}
                  onClick={() => generateBarcode(currentProduct.id)}
                >
                  Generate Barcode
                </Button>
              </div>
            ) : (
              <div className="text-center py-8">
                <ScanOutlined className="text-4xl text-gray-400 mb-4" />
                <p className="text-gray-500">Scan a barcode to view product information</p>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Scanned Items */}
      <Card
        title="Scanned Items"
        extra={
          <Space>
            <Button
              type="primary"
              icon={<CheckOutlined />}
              onClick={handleStockAdjustment}
              disabled={scannedProducts.length === 0}
            >
              Process Stock Adjustment
            </Button>
            <Button
              icon={<PrinterOutlined />}
              onClick={printLabels}
              disabled={scannedProducts.length === 0}
            >
              Print Labels
            </Button>
          </Space>
        }
      >
        <Table
          dataSource={scannedProducts}
          columns={scannedColumns}
          rowKey="barcode"
          pagination={false}
          locale={{
            emptyText: 'No items scanned yet'
          }}
        />
      </Card>

      {/* Stock Adjustment Modal */}
      <Modal
        title="Process Stock Adjustment"
        open={adjustmentModalVisible}
        onOk={processStockAdjustment}
        onCancel={() => setAdjustmentModalVisible(false)}
        width={600}
      >
        <Form form={form} layout="vertical" className="mt-4">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="adjustmentType"
                label="Adjustment Type"
                rules={[{ required: true, message: 'Please select adjustment type' }]}
              >
                <Select placeholder="Select type">
                  <Select.Option value="add">Add to Stock</Select.Option>
                  <Select.Option value="remove">Remove from Stock</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="location"
                label="Location"
                rules={[{ required: true, message: 'Please enter location' }]}
              >
                <Input placeholder="Enter location" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="reason"
            label="Reason"
            rules={[{ required: true, message: 'Please enter reason' }]}
          >
            <Input.TextArea
              rows={3}
              placeholder="Enter reason for stock adjustment"
            />
          </Form.Item>

          <div className="bg-gray-50 p-4 rounded">
            <Text strong>Items to be adjusted:</Text>
            <div className="mt-2 space-y-1">
              {scannedProducts.map(item => (
                <div key={item.barcode} className="flex justify-between">
                  <span>{item.name}</span>
                  <span>Qty: {item.quantity}</span>
                </div>
              ))}
            </div>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default BarcodeScanner;
